import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { AppRole } from '@/lib/roles';
import type { PlanTier, BillingStatus, BillingScope } from '@/lib/billing';
import { getEmailConfirmRedirectUrl } from '@/lib/authRedirect';

interface Profile {
  id: string;
  user_id: string;
  school_id: string | null;
  trust_id: string | null;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  schoolPlan: 'school' | 'individual' | null;  // derived from schools.individual_plan
  planTier: PlanTier | null;
  planSubTier: string | null;
  billingStatus: BillingStatus | null;
  billingScope: BillingScope | null;
  trialEndsAt: string | null;
  suspendedAt: string | null;
}

interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  key_stage_id: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  isLoading: boolean;
  hasRole: (role: AppRole) => boolean;
  isSuperAdmin: boolean;
  primaryRole: AppRole | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, planType?: 'school' | 'individual', productUpdates?: boolean) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  schoolPlan: 'school' | 'individual' | null;
  planTier: PlanTier | null;
  planSubTier: string | null;
  billingStatus: BillingStatus | null;
  billingScope: BillingScope | null;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  trustId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      // Fetch billing columns separately so a missing migration never breaks the core profile load.
      const [profileResult, rolesResult] = await Promise.all([
        supabase.from('profiles').select('*, schools(individual_plan)').eq('user_id', userId).single(),
        supabase.from('user_roles').select('*').eq('user_id', userId),
      ]);

      let profileData = profileResult.data as any;

      // If no profile row exists (sign-up upsert race), create it from auth metadata
      if (!profileData) {
        const authUser = (await supabase.auth.getUser()).data.user;
        if (authUser) {
          await supabase.from('profiles').upsert({
            user_id: userId,
            email: authUser.email ?? null,
            full_name: authUser.user_metadata?.full_name ?? null,
          }, { onConflict: 'user_id' });
          const retry = await supabase.from('profiles')
            .select('*, schools(individual_plan)')
            .eq('user_id', userId)
            .single();
          profileData = retry.data as any;
        }
      }

      // Attempt to fetch billing fields — fails gracefully if migration not yet applied.
      let billingFields: { plan_tier?: string; plan_sub_tier?: string; billing_status?: string; billing_scope?: string; trial_ends_at?: string; suspended_at?: string | null } = {};
      if (profileData?.school_id) {
        const { data: schoolBilling } = await supabase
          .from('schools')
          .select('plan_tier, plan_sub_tier, billing_status, billing_scope, trial_ends_at, suspended_at')
          .eq('id', profileData.school_id)
          .single();
        if (schoolBilling) billingFields = schoolBilling as typeof billingFields;
      }

      if (profileData) {
        const raw = profileData;
        const school = raw.schools as any;
        const individualPlan = school?.individual_plan;
        const p: Profile = {
          ...raw,
          trust_id: raw.trust_id ?? null,
          schoolPlan:
            individualPlan === true ? 'individual' :
            individualPlan === false ? 'school' :
            null,
          planTier: (billingFields.plan_tier as PlanTier) ?? null,
          planSubTier: billingFields.plan_sub_tier ?? null,
          billingStatus: (billingFields.billing_status as BillingStatus) ?? null,
          billingScope: (billingFields.billing_scope as BillingScope) ?? null,
          trialEndsAt: billingFields.trial_ends_at ?? null,
          suspendedAt: billingFields.suspended_at ?? null,
        };
        // Heal full_name if it was not written at sign-up (trigger race)
        if (!p.full_name) {
          const meta = (await supabase.auth.getUser()).data.user?.user_metadata;
          if (meta?.full_name) {
            await supabase.from('profiles').update({ full_name: meta.full_name }).eq('user_id', userId);
            p.full_name = meta.full_name as string;
          }
        }
        setProfile(p);
      }

      if (rolesResult.data) {
        setRoles(rolesResult.data as UserRole[]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer profile fetch with setTimeout to avoid deadlock
        if (session?.user) {
          // On a fresh sign-in, isLoading may already be false (set during the
          // initial no-session load). Reset it so Dashboard doesn't see a window
          // where user is set but roles are still empty — which would wrongly
          // trigger the /choose-plan redirect before super_admin roles arrive.
          if (event === 'SIGNED_IN') setIsLoading(true);
          setTimeout(() => {
            fetchProfile(session.user.id).finally(() => setIsLoading(false));
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const hasRole = (role: AppRole): boolean => {
    return roles.some(r => r.role === role);
  };

  const isSuperAdmin = roles.some(r => r.role === 'super_admin');

  const primaryRole = roles.length > 0 ? roles[0].role : null;

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string, planType?: 'school' | 'individual', productUpdates?: boolean) => {
    const redirectUrl = getEmailConfirmRedirectUrl();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          product_updates: productUpdates ?? false,
          ...(planType ? { plan_type: planType } : {}),
        },
      },
    });

    if (!error && data.user) {
      // Upsert profile — a DB trigger may have already created the row with full_name=null
      const { error: profileError } = await supabase.from('profiles').upsert({
        user_id: data.user.id,
        email,
        full_name: fullName,
      }, { onConflict: 'user_id' });

      if (profileError) {
        console.error('Error creating profile:', profileError);
      }
    }

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
  };

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchProfile(user.id);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        isLoading,
        hasRole,
        isSuperAdmin,
        primaryRole,
        signIn,
        signUp,
        signOut,
        refreshProfile,
        schoolPlan: profile?.schoolPlan ?? null,
        planTier: profile?.planTier ?? null,
        planSubTier: profile?.planSubTier ?? null,
        billingStatus: profile?.billingStatus ?? null,
        billingScope: profile?.billingScope ?? null,
        trialEndsAt: profile?.trialEndsAt ?? null,
        suspendedAt: profile?.suspendedAt ?? null,
        trustId: profile?.trust_id ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
